import { pipi } from '@baseValues'
import { ThreeEvent } from '@react-three/fiber'
import { useAppStore } from '@store'

type Props = {
  id: string
  position: [number, number, number]
  width: number
  thickness: number
  isSelected: boolean
}
export default function VertexHandler({ id, width, thickness }: Props) {
  const setMode = useAppStore.use.setMode()
  const mode = useAppStore((s) => s.mode)
  const start = useAppStore((store) => store.items.get(id)?.start)
  const [startx, startz] = start
  const closeArea = useAppStore.use.closeArea()
  const unselectAll = useAppStore.use.unselectAll()
  const setCameraMove = useAppStore.use.setCameraMove()
  const setBeingDrawnVertex = useAppStore.use.setBeingDrawnVertex()
  const currentObjectID = useAppStore.use.currentObjectID()
  const editWall = useAppStore.use.editWall()
  const editWallProperty = useAppStore.use.editWallProperty()
  const prevWall = useAppStore((store) => store.items.get(id)?.prevWall)
  const nextWall = useAppStore((store) => store.items.get(id)?.nextWall)
  const snapToWall = useAppStore.use.snapToWall()
  const addInnerAngle = useAppStore.use.addInnerAngle()
  const setCanDraw = useAppStore.use.setCanDraw()
  const isModalOpen = useAppStore((s) => s.isModalOpen)
  const mergeWalls = useAppStore.use.mergeWalls()
  const currentFirstWall = useAppStore.use.currentFirstWall()
  const vertexColor = useAppStore((s) => s.prefs?.['C/CirculoVertices'])

  function handleClick(e: ThreeEvent<MouseEvent>) {
    if (isModalOpen) return

    if (mode === 'MODE_DRAWING_WALL' && !e.object.name.includes('rightVertex')) {
      e.stopPropagation()
      setMode('MODE_IDLE')
      editWallProperty(id, { prevWall: currentObjectID })
      editWallProperty(currentObjectID, { nextWall: id, isFirst: false })
      editWall(startx, startz, currentObjectID)
      addInnerAngle(currentObjectID, id)
      closeArea()
      mergeWalls(currentFirstWall)
      unselectAll()
    }
    if (mode === 'MODE_DRAWING_WALL' && e.object.name.includes('leftVertex')) {
      e.stopPropagation()
    }
    if (mode === 'MODE_IDLE' && !prevWall) {
      e.stopPropagation()
      setMode('MODE_DRAGGING_VERTEX')
    }
  }
  function handlePointerDown(e: ThreeEvent<PointerEvent>) {
    e.stopPropagation()
    if (isModalOpen || mode !== 'MODE_IDLE') return
    setMode('MODE_DRAGGING_VERTEX')
  }
  function handlePointerUp(e: ThreeEvent<PointerEvent>) {
    e.stopPropagation()
    if (isModalOpen) return
    if (mode === 'MODE_DRAGGING_VERTEX') {
      setCameraMove(true)
      setMode('MODE_IDLE')
    }
  }

  function handleMove(e: ThreeEvent<PointerEvent>) {
    if (isModalOpen || mode !== 'MODE_DRAGGING_VERTEX') return
    if (e.object.name.includes('leftVertex')) {
      e.stopPropagation()
      setCameraMove(false)
      setMode('MODE_DRAGGING_VERTEX')
      setBeingDrawnVertex([id, e.object.name, prevWall])
    }

    if (e.object.name.includes('rightVertex')) {
      e.stopPropagation()
      setCameraMove(false)
      setMode('MODE_DRAGGING_VERTEX')
      setBeingDrawnVertex([id, e.object.name, nextWall])
    }
  }

  const BASE_Y = 2
  const isFirst = useAppStore((store) => store.items.get(id)?.isFirst)
  function snapToWallHandler(e: ThreeEvent<PointerEvent>) {
    if (isModalOpen || mode !== 'MODE_DRAWING_WALL') return
    e.stopPropagation()
    editWall(startx, startz, currentObjectID)
    snapToWall(id)
    mergeWalls(id)
  }
  function snapClick(e: ThreeEvent<MouseEvent>) {
    if (isModalOpen) return
    e.stopPropagation()
    setCanDraw(false)
    editWallProperty(id, { prevWall: currentObjectID, isFirst: false })
    editWallProperty(currentObjectID, { nextWall: id })
    editWall(startx, startz, currentObjectID)
    mergeWalls(currentFirstWall)
    setMode('MODE_IDLE')
    closeArea()
    mergeWalls(currentFirstWall)
    unselectAll()
    setCanDraw(true)
  }
  return (
    <>
      <mesh
        key={vertexColor}
        renderOrder={9998}
        onClick={handleClick}
        visible={true}
        position={[-width / 2, BASE_Y, thickness / 2]}
        rotation={[-Math.PI / 2, 0, 0]}
        name='leftVertex'
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerMove={handleMove}
        onPointerCancel={handlePointerDown}
      >
        <circleGeometry
          key={`leftVertex-${prevWall}-${id}-${width}-${thickness}-${nextWall}`}
          args={[75, 64, 0, pipi]}
        />
        <lineBasicMaterial
          color={vertexColor}
          toneMapped={false}
        />
      </mesh>
      {isFirst && (
        <mesh
          renderOrder={9999}
          onPointerEnter={snapToWallHandler}
          onClick={snapClick}
          visible={false}
          position={[-width / 2, BASE_Y + 2, thickness / 2]}
          rotation={[-Math.PI / 2, 0, 0]}
          name='leftSnapVertex'
        >
          <circleGeometry
            key={`leftSnapVertex-${prevWall}-${id}-${width}-${thickness}-${nextWall}`}
            args={[100, 64, 0, pipi]}
          />
          <lineBasicMaterial
            color='red'
            toneMapped={false}
          />
        </mesh>
      )}
      <mesh
        key={vertexColor}
        onClick={handleClick}
        renderOrder={9998}
        position={[width / 2, BASE_Y, thickness / 2]}
        rotation={[-Math.PI / 2, 0, 0]}
        key={`right-Vertex-${prevWall}-${id}-${width}-${thickness}-${nextWall}`}
        name='rightVertex'
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerMove={handleMove}
        onPointerCancel={handlePointerDown}
      >
        <circleGeometry args={[75, 64, 0, 6.283185307179586]} />
        <lineBasicMaterial
          color={vertexColor}
          toneMapped={false}
        />
      </mesh>
    </>
  )
}
